import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Package, ExternalLink, Clock, FileText, Check,
  ChevronLeft, ChevronRight, Plane
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ServiceCapabilityBadges from './ServiceCapabilityBadges';

interface ServiceProduct {
  id: string;
  name: string;
  product_type: string;
  description: string | null;
  price: number | null;
  currency?: string | null;
  certifications?: string[] | null;
  certification_basis?: string | null;
  photos?: string[] | null;
  compatible_helicopters?: string[] | null;
  part_number?: string | null;
  lead_time?: string | null;
  documentation_url?: string | null;
}

interface ServiceProductCardProps {
  product: ServiceProduct;
  formatPrice: (price?: number, currency?: string) => string | null;
}

const ServiceProductCard: React.FC<ServiceProductCardProps> = ({ product, formatPrice }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const photos = product.photos?.filter(Boolean) || [];
  const hasPhotos = photos.length > 0;
  const hasMultiplePhotos = photos.length > 1;

  const nextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Format lead time
  const formatLeadTime = (leadTime: string) => {
    const mapping: Record<string, string> = {
      'in_stock': 'In Stock',
      '1_2_weeks': '1-2 Weeks',
      '2_4_weeks': '2-4 Weeks',
      '1_2_months': '1-2 Months',
      '2_3_months': '2-3 Months',
      '3_6_months': '3-6 Months',
      '6_plus_months': '6+ Months',
      'made_to_order': 'Made to Order',
    };
    return mapping[leadTime] || leadTime.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format product type
  const formatProductType = (type: string) => {
    const mapping: Record<string, string> = {
      'stc': 'STC',
      'pma_part': 'PMA Part',
      'oem_part': 'OEM Part',
      'modification_kit': 'Mod Kit',
      'interior_component': 'Interior',
      'avionics_system': 'Avionics',
      'safety_equipment': 'Safety',
      'other': 'Other',
    };
    return mapping[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card className="overflow-hidden border-border/60 hover:border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 h-full">
      {/* Horizontal layout: Image left, Content right */}
      <div className="flex flex-row h-full">
        {/* Photo Section - Fixed width on left */}
        <div className="relative w-28 sm:w-36 shrink-0 bg-gradient-to-br from-muted/40 to-muted/20">
          {hasPhotos ? (
            <>
              <img
                src={photos[currentPhotoIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              {hasMultiplePhotos && (
                <>
                  <button
                    onClick={prevPhoto}
                    aria-label="Previous photo"
                    className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <button
                    onClick={nextPhoto}
                    aria-label="Next photo"
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                  {/* Photo count badge */}
                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                    {currentPhotoIndex + 1}/{photos.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[100px]">
              <Package className="w-8 h-8 text-muted-foreground/30" />
            </div>
          )}
        </div>

        {/* Content Section - Flexible right side */}
        <CardContent className="flex-1 p-3 flex flex-col gap-1.5 min-w-0">
          {/* Header: Type Badge + P/N */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge className="bg-primary/10 text-primary border-0 text-[9px] uppercase tracking-wide px-1.5 py-0">
              {formatProductType(product.product_type)}
            </Badge>
            {product.part_number && (
              <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1 py-0 rounded truncate max-w-[80px]">
                P/N: {product.part_number}
              </span>
            )}
          </div>

          {/* Product Name */}
          <h4 className="font-semibold text-sm leading-tight line-clamp-2">
            {product.name}
          </h4>

          {/* Price + Lead Time Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {product.price && (
              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                {formatPrice(product.price, product.currency || 'USD')}
              </span>
            )}
            {product.lead_time && (
              <Badge variant="secondary" className="text-[9px] gap-0.5 px-1.5 py-0">
                <Clock className="w-2.5 h-2.5" />
                {formatLeadTime(product.lead_time)}
              </Badge>
            )}
          </div>

          {/* Description - 2 lines max */}
          {product.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {product.description}
            </p>
          )}

          {/* Footer: Certifications + Docs button */}
          <div className="flex items-center gap-1.5 flex-wrap mt-auto pt-1">
            {/* Certifications - show first 2 */}
            {product.certifications && product.certifications.length > 0 && (
              <>
                {product.certifications.slice(0, 2).map((cert, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-[9px] bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700/50 text-emerald-700 dark:text-emerald-300 px-1.5 py-0"
                  >
                    <Check className="w-2.5 h-2.5 mr-0.5" />
                    {cert}
                  </Badge>
                ))}
                {product.certifications.length > 2 && (
                  <span className="text-[9px] text-muted-foreground">+{product.certifications.length - 2}</span>
                )}
              </>
            )}
            
            {/* Docs button - push to right */}
            {product.documentation_url && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 text-[9px] text-primary hover:text-primary/80 hover:bg-primary/10 ml-auto px-1.5"
                onClick={() => window.open(product.documentation_url!, '_blank')}
              >
                <FileText className="w-3 h-3 mr-0.5" />
                Docs
                <ExternalLink className="w-2.5 h-2.5 ml-0.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default ServiceProductCard;
